import React, { useState, useEffect } from "react";
import { Package, ClipboardList, Clock, Search } from "lucide-react";
import {
  getProductHistory,
  getOrderHistory,
  getProductsHistory,
  getOrdersHistory,
} from "../../api/history";
import { LoginHistory, ProductHistory, OrderHistory } from "../../types";
import { format } from "date-fns";
import { ko } from "date-fns/locale";

const unavailableReasonLabelMap: Record<string, string> = {
  OUT_OF_STOCK: "재고 부족",
  PRODUCT_NOT_FOUND: "상품 없음",
  PRODUCT_HIDDEN: "숨김 상품",
  SALES_NOT_STARTED: "판매 전",
  SALES_ENDED: "판매 종료",
  STORE_UNAVAILABLE: "매장 비노출",
  OPTION_NOT_FOUND: "옵션 없음",
};

export const HistoryTab: React.FC = () => {
  const [activeSubTab, setActiveSubTab] = useState<"PRODUCT" | "ORDER">(
    "PRODUCT",
  );
  const [productLogs, setProductLogs] = useState<ProductHistory[]>([]);
  const [orderLogs, setOrderLogs] = useState<OrderHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchId, setSearchId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 20;

  useEffect(() => {
    setCurrentPage(1);
    if (activeSubTab === "PRODUCT") {
      fetchProductsHistory();
    } else if (activeSubTab === "ORDER") {
      fetchOrdersHistory();
    }
  }, [activeSubTab]);

  const fetchProductsHistory = async () => {
    setLoading(true);
    try {
      const data = await getProductsHistory();
      setProductLogs(data);
    } catch (error) {
      console.error("Failed to fetch product history:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrdersHistory = async () => {
    setLoading(true);
    try {
      const data = await getOrdersHistory();
      setOrderLogs(data);
    } catch (error) {
      console.error("Failed to fetch order history:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    setCurrentPage(1);
    try {
      if (activeSubTab === "PRODUCT") {
        const data = await getProductsHistory(searchId);
        setProductLogs(data);
      } else if (activeSubTab === "ORDER") {
        const data = await getOrdersHistory(searchId);
        setOrderLogs(data);
      }
    } catch (error) {
      console.error("Failed to fetch history:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex bg-white p-1 rounded-xl shadow-sm border w-fit">
        {[
          { id: "PRODUCT", icon: Package, label: "상품 수정/취소" },
          { id: "ORDER", icon: ClipboardList, label: "주문 변경" },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id as any)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${
              activeSubTab === tab.id
                ? "bg-gray-900 text-white shadow-md"
                : "text-gray-500 hover:bg-gray-50"
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {(activeSubTab === "PRODUCT" || activeSubTab === "ORDER") && (
        <div className="flex gap-2">
          <div className="relative flex-1 max-w-md">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              placeholder={
                activeSubTab === "PRODUCT"
                  ? "상품ID 또는 상품명 입력"
                  : "주문ID 또는 고객명 입력"
              }
              className="w-full pl-10 pr-4 py-2 bg-white border rounded-xl text-sm focus:ring-2 focus:ring-gray-900 outline-none"
              value={searchId}
              onChange={(e) => setSearchId(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
          </div>
          <button
            onClick={handleSearch}
            className="px-6 py-2 bg-gray-900 text-white rounded-xl text-sm font-bold hover:bg-gray-800 transition-all"
          >
            조회
          </button>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        {loading ? (
          <div className="p-20 text-center text-gray-400">로딩 중...</div>
        ) : (
          <>
            <table className="w-full text-left">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-4 text-xs font-black text-gray-400 uppercase tracking-wider">
                    시간
                  </th>
                  <th className="px-6 py-4 text-xs font-black text-gray-400 uppercase tracking-wider">
                    대상/아이디
                  </th>
                  <th className="px-6 py-4 text-xs font-black text-gray-400 uppercase tracking-wider">
                    동작/상태
                  </th>
                  <th className="px-6 py-4 text-xs font-black text-gray-400 uppercase tracking-wider">
                    기타 정보
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {activeSubTab === "PRODUCT" &&
                  productLogs
                    .slice(
                      (currentPage - 1) * ITEMS_PER_PAGE,
                      currentPage * ITEMS_PER_PAGE,
                    )
                    .map((log) => {
                      const actionMap: Record<string, string> = {
                        CREATE: "생성",
                        UPDATE: "수정",
                        DELETE: "삭제",
                        CLOSE: "마감",
                        OPEN: "오픈",
                        STOCK_DEDUCTED_BY_ORDER: "재고 차감 (주문)",
                        STOCK_RESTORED_BY_ORDER_CANCEL: "재고 복구 (취소)",
                        STOCK_RESTORED_BY_ORDER: "재고 복구 (변경)",
                        STOCK_RESTORED_BY_ORDER_DELETE: "재고 복구 (삭제)",
                      };
                      return (
                        <tr
                          key={log.id}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">
                            {format(
                              new Date(log.createdAt),
                              "yyyy-MM-dd HH:mm:ss",
                              { locale: ko },
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {log.product?.name || "알 수 없는 상품"}
                            <span className="block text-xs text-gray-300">
                              {log.productId}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span
                              className={`px-2 py-1 rounded-md text-xs font-bold ${
                                log.action === "CREATE"
                                  ? "bg-green-50 text-green-600"
                                  : log.action === "DELETE"
                                    ? "bg-red-50 text-red-600"
                                    : log.action === "CLOSE"
                                      ? "bg-orange-50 text-orange-600"
                                      : "bg-gray-100 text-gray-600"
                              }`}
                            >
                              {actionMap[log.action] || log.action}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-xs text-gray-400">
                            <span className="font-bold text-gray-600">
                              {log.modifierRole === "SELLER"
                                ? "판매자"
                                : log.modifierRole === "ADMIN"
                                  ? "관리자"
                                  : "고객"}
                            </span>
                          </td>
                        </tr>
                      );
                    })}

                {activeSubTab === "ORDER" &&
                  orderLogs
                    .slice(
                      (currentPage - 1) * ITEMS_PER_PAGE,
                      currentPage * ITEMS_PER_PAGE,
                    )
                    .map((log) => {
                      const statusMap: Record<string, string> = {
                        PENDING: "주문완료",
                        READY: "상품준비",
                        COMPLETED: "수령완료",
                        CANCELLED: "취소됨",
                      };
                      const actionMap: Record<string, string> = {
                        UPDATE_ITEMS: "품목 변경",
                        UPDATE_PRICE: "상품 가격 변경",
                        UPDATE_STATUS: "상태 변경",
                        UPDATE_INFO: "정보 변경",
                        CREATE: "주문 생성",
                        CANCEL: "주문 취소",
                        BATCH_UPDATE: "일괄 변경",
                        PARTIAL_SPLIT_COMPLETE: "일부 품목 상태 변경",
                      };
                      return (
                        <tr
                          key={log.id}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">
                            {format(
                              new Date(log.createdAt),
                              "yyyy-MM-dd HH:mm:ss",
                              { locale: ko },
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {log.order?.customerName || "알 수 없는 주문"}
                            <span className="block text-xs text-gray-300">
                              {log.orderId}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-400">
                                {statusMap[log.oldStatus || ""] ||
                                  log.oldStatus ||
                                  "-"}
                              </span>
                              <span className="text-gray-300">→</span>
                              <span
                                className={`px-2 py-1 rounded-md text-xs font-bold ${
                                  log.newStatus === "CANCELLED"
                                    ? "bg-red-50 text-red-600"
                                    : "bg-blue-50 text-blue-600"
                                }`}
                              >
                                {statusMap[log.newStatus] || log.newStatus}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-xs text-gray-500 font-medium">
                            <div className="flex items-center gap-1.5 mb-1.5">
                              <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px] font-bold">
                                {log.modifierRole === "SELLER"
                                  ? "판매자"
                                  : log.modifierRole === "ADMIN"
                                    ? "관리자"
                                    : "고객"}
                              </span>
                              <span className="text-gray-900 font-black">
                                {actionMap[log.action || ""] || log.action}
                              </span>
                            </div>
                            {log.details?.itemChanges && (
                              <div className="mt-2 p-2 bg-gray-50 rounded-lg space-y-1 border border-gray-100">
                                {log.details.itemChanges.map(
                                  (change: any, idx: number) => (
                                    <div
                                      key={idx}
                                      className="flex justify-between items-center text-[10px]"
                                    >
                                      <span className="font-bold text-gray-600 truncate max-w-[120px]">
                                        {change.name}
                                      </span>
                                      {change.changeKind === "STATUS_ONLY" ||
                                      change.diff === 0 ? (
                                        <span className="font-black shrink-0 text-slate-500">
                                          상태 변경 · {change.newQty}개
                                        </span>
                                      ) : (
                                        <span
                                          className={`font-black shrink-0 ${change.diff > 0 ? "text-green-600" : "text-red-500"}`}
                                        >
                                          {change.oldQty} → {change.newQty} (
                                          {change.diff > 0 ? "+" : ""}
                                          {change.diff})
                                        </span>
                                      )}
                                    </div>
                                  ),
                                )}
                              </div>
                            )}
                            {Array.isArray(log.details?.priceChanges) &&
                              log.details.priceChanges.length > 0 && (
                                <div className="mt-2 p-2 bg-emerald-50 rounded-lg space-y-1 border border-emerald-100">
                                  {log.details.priceChanges.map(
                                    (change: any, idx: number) => (
                                      <div key={idx} className="text-[10px] font-bold text-gray-600">
                                        <span className="text-gray-900">{change.name}</span>{" "}
                                        {change.oldUnitPrice.toLocaleString()}원 →{" "}
                                        <span className="text-emerald-700">
                                          {change.newUnitPrice.toLocaleString()}원
                                        </span>{" "}
                                        · {change.quantity}개
                                      </div>
                                    ),
                                  )}
                                </div>
                              )}
                            {log.details?.newPrice !== undefined &&
                              log.details?.oldPrice !== undefined && (
                                <div className="mt-1 text-[10px] font-bold text-gray-400">
                                  금액: {log.details.oldPrice.toLocaleString()}{" "}
                                  → {log.details.newPrice.toLocaleString()}
                                </div>
                              )}
                            {log.details?.requestId && (
                              <div className="mt-1 text-[10px] font-bold text-gray-400">
                                요청 ID: {log.details.requestId}
                              </div>
                            )}
                            {Array.isArray(log.details?.unavailableItems) &&
                              log.details.unavailableItems.length > 0 && (
                                <div className="mt-2 p-2 bg-amber-50 rounded-lg space-y-1 border border-amber-100">
                                  {log.details.unavailableItems.map(
                                    (item: any, idx: number) => (
                                      <div
                                        key={`${item.productId || idx}-${idx}`}
                                        className="text-[10px] text-amber-900"
                                      >
                                        <span className="font-black">
                                          {item.name}
                                        </span>
                                        <span className="mx-1 text-amber-400">
                                          ·
                                        </span>
                                        <span className="font-semibold">
                                          {unavailableReasonLabelMap[
                                            item.reason
                                          ] || item.reason}
                                        </span>
                                        <span className="mx-1 text-amber-400">
                                          ·
                                        </span>
                                        <span>
                                          요청 {item.requestedQuantity}개 / 가능{" "}
                                          {item.availableQuantity}개
                                        </span>
                                      </div>
                                    ),
                                  )}
                                </div>
                              )}
                            {Array.isArray(
                              log.details?.duplicateOrderIdsCancelled,
                            ) &&
                              log.details.duplicateOrderIdsCancelled.length >
                                0 && (
                                <div className="mt-1 text-[10px] font-bold text-gray-400">
                                  병합 취소 주문:{" "}
                                  {
                                    log.details.duplicateOrderIdsCancelled
                                      .length
                                  }
                                  건
                                </div>
                              )}
                          </td>
                        </tr>
                      );
                    })}

                {((activeSubTab === "PRODUCT" && productLogs.length === 0) ||
                  (activeSubTab === "ORDER" && orderLogs.length === 0)) &&
                  !loading && (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-6 py-20 text-center text-gray-400 italic"
                      >
                        조회된 이력이 없습니다.
                      </td>
                    </tr>
                  )}
              </tbody>
            </table>
            {(() => {
              const logs = activeSubTab === "PRODUCT" ? productLogs : orderLogs;
              const totalPages = Math.max(
                1,
                Math.ceil(logs.length / ITEMS_PER_PAGE),
              );
              if (totalPages <= 1) return null;
              return (
                <div className="flex justify-center items-center gap-2 px-6 py-4 border-t">
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(1, prev - 1))
                    }
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 rounded-lg border text-xs font-bold bg-white text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    이전
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(
                        (p) =>
                          p === 1 ||
                          p === totalPages ||
                          Math.abs(p - currentPage) <= 2,
                      )
                      .map((p, i, arr) => (
                        <React.Fragment key={p}>
                          {i > 0 && arr[i - 1] !== p - 1 ? (
                            <span className="text-gray-400 text-xs px-1">
                              ...
                            </span>
                          ) : null}
                          <button
                            onClick={() => setCurrentPage(p)}
                            className={`min-w-[32px] h-8 rounded-lg text-xs font-bold transition-colors ${currentPage === p ? "bg-gray-900 text-white shadow-sm" : "bg-white border text-gray-600 hover:bg-gray-50"}`}
                          >
                            {p}
                          </button>
                        </React.Fragment>
                      ))}
                  </div>
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 rounded-lg border text-xs font-bold bg-white text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    다음
                  </button>
                  <span className="text-xs text-gray-400 font-bold ml-2">
                    {currentPage} / {totalPages} 페이지
                  </span>
                </div>
              );
            })()}
          </>
        )}
      </div>
    </div>
  );
};
